import styles from "./CartProductCard.module.css"
import { X, Minus, Plus, PencilLine } from 'lucide-react';
import { getPrimaryProductImage } from "../../../services/productImageService";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { formatCurrencyFromCzk } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { CartStorageItems } from "../../../types/storageItem.types";
import { normalizeDate } from "../../../utils/calendar";
import { useMemo, useCallback } from "react";
import React, { useRef, useState, useEffect } from 'react';
import { cartChange } from "../../../utils/cart";
import { CartItem } from "../../../types/cart.types";
import { getCzechPieces } from "../../../utils/translation";
import Tooltip from "../../Tooltip/Tooltip";
import { defaultMinTime, defaultMaxTime } from "../../../constants/times";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import { getAdvanceDays } from "../../../utils/products";
/**
* Cart card for storage items
*
* @param props CartStorageItems
* @param {StorageItem} props.product - Product
* @param {string} props.time - Delivery time
* @param {string} props.date - Delivery date
* @returns TSX.Element
*/
const CartExtraCard = ({ product, time, date, popup = true }: CartStorageItems) => {
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t, i18n } = useTranslation(); // Translation
    const {isPublicView} = usePublicCatalog();
    const { openDetail } = useDetailContext(); // Detail context
    const { openTimeModal } = useTimeModalActions(); // Time modal toggle 
    const { selectedCurrency } = usePreferences(); // Curent selected currency    
    // Find the correct items
    const serverItem: CartItem | undefined = useMemo(() => {
        const itemsForDate: CartItem[] = cartData?.items?.[date] ?? [];
        return itemsForDate.find(
        (cartProduct) => cartProduct.id === product.id && cartProduct.time === time && cartProduct.category === "storage"
        );
    }, [cartData?.items, date, product.id, time]);

    // Cart amount
    const [cartAmount, setCartAmount] = useState<number>(() => serverItem?.amount ?? 0);
    const [cartTime, setCartTime] = useState<string[]>(() => serverItem?.time.split(" - ") ?? ["7:00", "7:45"]);
    
    // Track if the cart data amount is changing
    const isAmountChangingRef = useRef(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Update cartAmount on CartData change and not changing - get rid of number flash
    useEffect(() => {
        if (!cartLoaded) return;
        const next = serverItem?.amount ?? 0;
        if (!isAmountChangingRef.current && next !== cartAmount) {
            setCartAmount(next);
        }
    }, [cartLoaded, serverItem?.amount]); 
    
    // Runs every time the users adds or deletes - reruns the timeout till the clicks dont stop - updates afterwards
    const debouncedReload = useMemo(() => {
        let refreshTimeout: number | null = null;
        return () => {
            if (refreshTimeout) window.clearTimeout(refreshTimeout);
            refreshTimeout = window.setTimeout(() => {
                reloadCart();
                refreshTimeout = null;
            }, 250);
        };
    }, [reloadCart]);

    // Change cart amount - using callback and Promise.resolve to unset changing only after the request is complete
    const handleCart = useCallback(
        (amount: number) => {
            if (isAmountChangingRef.current) return;
            setCartAmount(amount);           
            isAmountChangingRef.current = true; // Set changing ref
            setIsUpdating(true);
            Promise.resolve(
                cartChange(
                    cartData,
                    product.id,
                    normalizeDate(date),
                    time,
                    amount,
                    "storage",
                    normalizeDate(date),
                    time,
                    debouncedReload
                )
            )
            .finally(() => {
            isAmountChangingRef.current = false; // Unset changing ref
            setIsUpdating(false);
            });
        },
        [cartData, product.id, normalizeDate(date), time, debouncedReload]
    );



    const linePriceWithoutTax =
        typeof serverItem?.price_per_piece === "number"
            ? serverItem.price_per_piece * cartAmount
            : product.pricing?.price_without_tax != null
                ? product.pricing.price_without_tax * cartAmount
                : (typeof serverItem?.price_without_tax === "number" ? serverItem.price_without_tax : null);
    const noDphText =
        linePriceWithoutTax != null
            ? formatCurrencyFromCzk(linePriceWithoutTax, czk_eur_rate, selectedCurrency)
            : "N/A"; // Total price
    const buttonRef = useRef<HTMLDivElement>(null)
    const actionButtonsRef = useRef<HTMLDivElement>(null)

    // Handle opening detail
    const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!buttonRef.current?.contains(e.target as Node) && !actionButtonsRef.current?.contains(e.target as Node)) {
            openDetail && openDetail({id: product.id, type: "storage", time: time, date: date }, false, popup); 
        }
    }

    // Sample time limit
    const minTime = (product.order_from && product.order_from !== "")  ? product.order_from : defaultMinTime
    const maxTime = (product.order_to && product.order_to !== "")  ? product.order_to : defaultMaxTime

    // Order in advance
    const advance = getAdvanceDays(product);

    let piecesWord = "piece";
    if (i18n.language === "cz") {
        piecesWord = getCzechPieces(product.min_order ?? 1, "amount");
    }else {
        if (product.min_order && product.min_order > 1 || product.min_order === 0) {
            piecesWord = "pieces"
        }
    }

    let granularityWord = "piece";
    if (i18n.language === "cz") {
        granularityWord = getCzechPieces(product.granularity, "granularity");
    }else {
        if (product.granularity && product.granularity > 1 || product.granularity === 0) {
            granularityWord = "pieces"
        }
    }
    const tooltip =
    cartAmount <= (product.min_order?? 1) ?(i18n.language === "en"
            ? `Minimal order amount is ${product.min_order ?? 1} ${piecesWord}`
            : `Minimální množství do objednávky je ${product.min_order ?? 1} ${piecesWord}`) : (
                i18n.language === "en"
                ? `Service must be added in the groups of ${product.granularity ?? 1} ${granularityWord}`
                : `Služba musí být přidána po ${product.granularity ?? 1} ${granularityWord}`
            );
    const ref = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const showAmountControls = cartAmount > 0 && !isPublicView;

    return(
        <>
        <div className={styles.CartProduct} key={product.id} onClick={handleOpen}>
            <div className={styles.CartProductImage} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}></div>
            <div className={styles.CartProductContent}>
                <div className={styles.CartProductInfo}>
                    <p className={styles.CartProductTitle}>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                    <div className={styles.CartProductButtons} ref={actionButtonsRef}>
                        <button
                            className={styles.CartProductButton}
                            onClick={() =>
                                openTimeModal(
                                    {
                                        type: "single",
                                        date: normalizeDate(date),
                                        time: time,
                                        productId: product.id,
                                        prodType: "storage",
                                        restrictions: { min: minTime, max: maxTime },
                                        advance: advance,
                                    },
                                    { openOnTop: popup }
                                )
                            }
                        >
                            <PencilLine/>
                        </button>
                        {showAmountControls && (
                            <button className={styles.CartProductButton} onClick={()=>cartChange(cartData, product.id, normalizeDate(date), time, 0, "storage", undefined, undefined, reloadCart)} >
                                <X/>
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.CartProductOrderInfo}>
                        {showAmountControls && (
                            <div className={styles.CartProductAmount} ref={buttonRef}  onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} >
                                <button className={ `${styles.CartProductAmountDecrease}`} onClick={()=>{cartAmount > (product?.min_order ?? 1) ? handleCart(cartAmount - (1*(product.granularity??1))) : handleCart(0)}} disabled={isPublicView || isUpdating || cartAmount <= 0}>
                                    <Minus/>
                                </button>
                                <div className={styles.CartProductAmountText}>
                                    <p>{cartAmount}</p>
                                </div>
                                <button disabled={(cartAmount >= ((product.max_order === null || product.max_order === 0) ? 100 : product.max_order)) || isPublicView || isUpdating} className={styles.CartProductAmountIncrease} onClick={()=>handleCart(cartAmount + (1*(product.granularity??1)))}>
                                    <Plus/>
                                </button>
                                {showTooltip && <Tooltip desc={tooltip} className={styles.Tooltips} targetRef={buttonRef}>{tooltip}</Tooltip>}
                            </div>
                        )}
                        <p className={styles.CartProductPrice}> {t("products.no_dph_short")} {noDphText}</p>
                </div>
            </div>
        </div>
        </>
    )
}   

export default CartExtraCard
